import { ArrowRight, Zap, ShoppingCart, TrendingDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";

const AnimatedNumber = ({ target, suffix = "", duration = 1800 }) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            const startTime = performance.now();
            const step = (now) => {
              const elapsed = now - startTime;
              const progress = Math.min(elapsed / duration, 1);
              // ease-out cubic
              const eased = 1 - Math.pow(1 - progress, 3);
              setValue(Math.round(target * eased));
              if (progress < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration]);

  return (
    <span ref={ref} data-testid={`stat-${target}`}>
      {value}
      {suffix}
    </span>
  );
};

const Hero = () => {
  const { t } = useTranslation();
  const scrollToQuote = () => {
    const element = document.getElementById("quote");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative h-screen flex items-start justify-center overflow-hidden pt-24 sm:pt-28">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1724582586529-62622e50c0b3"
          alt="Modern Home Renovation"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 lg:px-8">
        <div className="max-w-3xl">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            {t('hero.title1')}
            <span className="block mt-2">
              <span className="hero-unbeatable inline-block relative">
                {t('hero.title2')}
              </span>
            </span>
          </h1>
          <style>{`
            .hero-unbeatable {
              background: linear-gradient(90deg, #3da5ba 0%, #5ec4d9 50%, #3da5ba 100%);
              background-size: 200% auto;
              -webkit-background-clip: text;
              background-clip: text;
              -webkit-text-fill-color: transparent;
              color: transparent;
              animation: hero-shimmer 3s linear infinite, hero-pulse 2.4s ease-in-out infinite;
              text-shadow: 0 0 28px rgba(61, 165, 186, 0.35);
              filter: drop-shadow(0 0 14px rgba(61, 165, 186, 0.55));
            }
            .hero-unbeatable::after {
              content: '';
              position: absolute;
              left: 0;
              bottom: -6px;
              height: 4px;
              width: 100%;
              background: linear-gradient(90deg, transparent 0%, #3da5ba 30%, #5ec4d9 50%, #3da5ba 70%, transparent 100%);
              border-radius: 4px;
              transform-origin: left center;
              animation: hero-underline 3s ease-in-out infinite;
            }
            @keyframes hero-shimmer {
              0%   { background-position: 0% center; }
              100% { background-position: 200% center; }
            }
            @keyframes hero-pulse {
              0%, 100% { filter: drop-shadow(0 0 14px rgba(61, 165, 186, 0.55)); }
              50%      { filter: drop-shadow(0 0 26px rgba(94, 196, 217, 0.95)); }
            }
            @keyframes hero-underline {
              0%   { transform: scaleX(0); opacity: 0; }
              30%  { transform: scaleX(1); opacity: 1; }
              70%  { transform: scaleX(1); opacity: 1; }
              100% { transform: scaleX(0) translateX(100%); opacity: 0; }
            }
          `}</style>
          <p className="text-xl md:text-2xl text-gray-200 mb-6 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          {/* Value Badges */}
          <div className="flex flex-nowrap gap-1.5 sm:gap-3 mb-6 sm:mb-8" data-testid="hero-badges">
            <div className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-2 py-1 sm:px-4 sm:py-2 whitespace-nowrap shrink-0">
              <Zap size={11} className="text-[#3da5ba] sm:hidden" />
              <Zap size={16} className="text-[#3da5ba] hidden sm:block" />
              <span className="text-white text-[9px] sm:text-sm font-medium">{t('hero.badge1')}</span>
            </div>
            <div className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-2 py-1 sm:px-4 sm:py-2 whitespace-nowrap shrink-0">
              <ShoppingCart size={11} className="text-[#3da5ba] sm:hidden" />
              <ShoppingCart size={16} className="text-[#3da5ba] hidden sm:block" />
              <span className="text-white text-[9px] sm:text-sm font-medium">{t('hero.badge2')}</span>
            </div>
            <div className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-2 py-1 sm:px-4 sm:py-2 whitespace-nowrap shrink-0">
              <TrendingDown size={11} className="text-[#3da5ba] sm:hidden" />
              <TrendingDown size={16} className="text-[#3da5ba] hidden sm:block" />
              <span className="text-white text-[9px] sm:text-sm font-medium">{t('hero.badge3')}</span>
            </div>
          </div>

          {/* Trust Indicators - one line on all viewports */}
          <div className="flex flex-nowrap gap-3 sm:gap-6 mb-6" data-testid="hero-stats">
            <div className="shrink-0">
              <div className="text-2xl sm:text-3xl font-bold text-white leading-none whitespace-nowrap">
                <AnimatedNumber target={250} suffix="+" />
              </div>
              <div className="text-gray-300 text-[11px] sm:text-sm mt-1.5 whitespace-nowrap">{t('hero.stat1')}</div>
            </div>
            <div className="border-l border-white/20 pl-3 sm:pl-6 shrink-0">
              <div className="text-2xl sm:text-3xl font-bold text-white leading-none whitespace-nowrap">
                <AnimatedNumber target={15} suffix="+" />
              </div>
              <div className="text-gray-300 text-[11px] sm:text-sm mt-1.5 whitespace-nowrap">{t('hero.stat2')}</div>
            </div>
            <div className="border-l border-white/20 pl-3 sm:pl-6 shrink-0">
              <div className="text-2xl sm:text-3xl font-bold text-white leading-none whitespace-nowrap">
                <AnimatedNumber target={100} suffix="%" />
              </div>
              <div className="text-gray-300 text-[11px] sm:text-sm mt-1.5 whitespace-nowrap">{t('hero.stat3')}</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={scrollToQuote}
              size="lg"
              className="bg-[#1a7a8a] hover:bg-[#156a78] text-white text-lg px-8 py-6 group"
            >
              {t('hero.cta1')}
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
            <Button
              onClick={() => {
                const element = document.getElementById("services");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 text-lg px-8 py-6"
            >
              {t('hero.cta2')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
